import { baseApi } from './baseApi';
import {
  Category,
  Product,
  ProductVariant,
  ProductWithVariants,
  ProductStock,
  VariantGroup,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateProductRequest,
  UpdateProductRequest,
  CreateProductVariantRequest,
  UpdateProductVariantRequest,
  LinkProductInventoryRequest,
} from '../types/products';

export const productsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Categories endpoints
    getCategories: builder.query<Category[], { activeOnly?: boolean }>({
      query: ({ activeOnly = false }) => `/categories?activeOnly=${activeOnly}`,
      providesTags: ['Category'],
    }),
    getCategory: builder.query<Category, string>({
      query: (id) => `/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Category', id }],
    }),
    createCategory: builder.mutation<Category, CreateCategoryRequest>({
      query: (newCategory) => ({
        url: '/categories',
        method: 'POST',
        body: newCategory,
      }),
      invalidatesTags: ['Category'],
    }),
    updateCategory: builder.mutation<Category, { id: string } & UpdateCategoryRequest>({
      query: ({ id, ...patch }) => ({
        url: `/categories/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Category', id },
        'Category' // Invalidate list cache to refresh all queries
      ],
    }),
    deleteCategory: builder.mutation<void, string>({
      query: (id) => ({
        url: `/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Category'],
    }),

    // Products endpoints
    getProducts: builder.query<Product[], { availableOnly?: boolean }>({
      query: ({ availableOnly = false }) => `/products?availableOnly=${availableOnly}`,
      providesTags: ['Product'],
    }),
    getProductsByCategory: builder.query<Product[], string>({
      query: (categoryId) => `/products/category/${categoryId}`,
      providesTags: ['Product'],
    }),
    getProduct: builder.query<Product, string>({
      query: (id) => `/products/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Product', id }],
    }),
    createProduct: builder.mutation<Product, CreateProductRequest>({
      query: (newProduct) => ({
        url: '/products',
        method: 'POST',
        body: newProduct,
      }),
      // MenuEditor: el lienzo del editor del menú digital muestra estos mismos
      // productos, así que un alta/edición/baja tiene que refrescarlo.
      invalidatesTags: ['Product', 'MenuEditor'],
    }),
    updateProduct: builder.mutation<Product, { id: string } & UpdateProductRequest>({
      query: ({ id, ...patch }) => ({
        url: `/products/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Product', id },
        'Product', // Invalidate list cache to refresh all queries
        'MenuEditor',
      ],
    }),
    updateProductAvailability: builder.mutation<void, { id: string; isAvailable: boolean }>({
      query: ({ id, isAvailable }) => ({
        url: `/products/${id}/availability`,
        method: 'PATCH',
        body: { isAvailable },
      }),
      invalidatesTags: ['Product'],
    }),
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Product', 'MenuEditor'],
    }),

    // Product Variants endpoints
    getProductWithVariants: builder.query<ProductWithVariants, string>({
      query: (id) => `/products/${id}/with-variants`,
      providesTags: (_result, _error, id) => [
        { type: 'Product', id },
        { type: 'ProductVariant', id: 'LIST' }
      ],
    }),
    getProductVariants: builder.query<ProductVariant[], string>({
      query: (productId) => `/products/${productId}/variants`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ProductVariant' as const, id })),
              { type: 'ProductVariant', id: 'LIST' },
            ]
          : [{ type: 'ProductVariant', id: 'LIST' }],
    }),
    getProductVariant: builder.query<ProductVariant, string>({
      query: (variantId) => `/products/variants/${variantId}`,
      providesTags: (_result, _error, id) => [{ type: 'ProductVariant', id }],
    }),
    createProductVariant: builder.mutation<ProductVariant, CreateProductVariantRequest>({
      query: (newVariant) => ({
        url: '/products/variants',
        method: 'POST',
        body: newVariant,
      }),
      // MenuEditor: los grupos de modificadores se publican desde el editor del
      // menú digital, que puede estar montado en otra pestaña de la misma
      // pantalla. Sin esto, un grupo creado desde Productos no aparecía ahí.
      invalidatesTags: [{ type: 'ProductVariant', id: 'LIST' }, 'MenuEditor'],
    }),
    updateProductVariant: builder.mutation<ProductVariant, { id: string } & UpdateProductVariantRequest>({
      query: ({ id, ...patch }) => ({
        url: `/products/variants/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ProductVariant', id },
        { type: 'ProductVariant', id: 'LIST' }, // Invalidate list cache
      ],
    }),
    deleteProductVariant: builder.mutation<void, string>({
      query: (id) => ({
        url: `/products/variants/${id}`,
        method: 'DELETE',
      }),
      // MenuEditor: los grupos de modificadores se publican desde el editor del
      // menú digital, que puede estar montado en otra pestaña de la misma
      // pantalla. Sin esto, un grupo creado desde Productos no aparecía ahí.
      invalidatesTags: [{ type: 'ProductVariant', id: 'LIST' }, 'MenuEditor'],
    }),

    // Variant Groups
    getVariantGroups: builder.query<VariantGroup[], string>({
      query: (productId) => `/products/${productId}/variant-groups`,
      providesTags: [{ type: 'ProductVariant', id: 'LIST' }],
    }),
    // Todos los del restaurante en una sola llamada: la caja offline los guarda para
    // poder poner precio a un renglón con variantes sin red.
    getAllVariantGroups: builder.query<VariantGroup[], void>({
      query: () => '/products/variant-groups',
      providesTags: [{ type: 'ProductVariant', id: 'LIST' }],
    }),
    // Todas las variantes, también las sueltas (sin grupo, anteriores a los grupos), que no
    // vienen en ningún grupo. La caja offline las necesita para el modal y el precio.
    getAllProductVariants: builder.query<ProductVariant[], void>({
      query: () => '/products/variants',
      providesTags: [{ type: 'ProductVariant', id: 'LIST' }],
    }),
    createVariantGroup: builder.mutation<VariantGroup, { productId: string; name: string; selectionMode: string; isRequired: boolean; sortOrder: number; variants?: { name: string; priceModifier: number }[] }>({
      query: (data) => ({
        url: '/products/variant-groups',
        method: 'POST',
        body: data,
      }),
      // MenuEditor: los grupos de modificadores se publican desde el editor del
      // menú digital, que puede estar montado en otra pestaña de la misma
      // pantalla. Sin esto, un grupo creado desde Productos no aparecía ahí.
      invalidatesTags: [{ type: 'ProductVariant', id: 'LIST' }, 'MenuEditor'],
    }),
    updateVariantGroup: builder.mutation<VariantGroup, { id: string; name: string; selectionMode: string; isRequired: boolean; sortOrder: number }>({
      query: ({ id, ...data }) => ({
        url: `/products/variant-groups/${id}`,
        method: 'PUT',
        body: data,
      }),
      // MenuEditor: los grupos de modificadores se publican desde el editor del
      // menú digital, que puede estar montado en otra pestaña de la misma
      // pantalla. Sin esto, un grupo creado desde Productos no aparecía ahí.
      invalidatesTags: [{ type: 'ProductVariant', id: 'LIST' }, 'MenuEditor'],
    }),
    deleteVariantGroup: builder.mutation<void, string>({
      query: (id) => ({
        url: `/products/variant-groups/${id}`,
        method: 'DELETE',
      }),
      // MenuEditor: los grupos de modificadores se publican desde el editor del
      // menú digital, que puede estar montado en otra pestaña de la misma
      // pantalla. Sin esto, un grupo creado desde Productos no aparecía ahí.
      invalidatesTags: [{ type: 'ProductVariant', id: 'LIST' }, 'MenuEditor'],
    }),

    // Import
    importProducts: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/products/import',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Product', 'Category', 'MenuEditor'],
    }),

    // Product-Inventory Bridge endpoints
    getProductsWithStock: builder.query<ProductStock[], void>({
      query: () => '/products/with-stock',
      providesTags: ['Product', 'InventoryItem', 'Recipe'],
    }),
    linkProductToInventory: builder.mutation<void, { id: string; data: LinkProductInventoryRequest }>({
      query: ({ id, data }) => ({
        url: `/products/${id}/inventory-link`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Product', 'InventoryItem'],
    }),
    unlinkProductFromInventory: builder.mutation<void, string>({
      query: (id) => ({
        url: `/products/${id}/inventory-link`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Product', 'InventoryItem'],
    }),
  }),
});

export const {
  // Categories hooks
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,

  // Products hooks
  useGetProductsQuery,
  useGetProductsByCategoryQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUpdateProductAvailabilityMutation,
  useDeleteProductMutation,

  // Product Variants hooks
  useGetProductWithVariantsQuery,
  useGetProductVariantsQuery,
  useGetProductVariantQuery,
  useCreateProductVariantMutation,
  useUpdateProductVariantMutation,
  useDeleteProductVariantMutation,

  // Variant Groups hooks
  useGetVariantGroupsQuery,
  useGetAllVariantGroupsQuery,
  useCreateVariantGroupMutation,
  useUpdateVariantGroupMutation,
  useDeleteVariantGroupMutation,

  // Import hooks
  useImportProductsMutation,

  // Product-Inventory Bridge hooks
  useGetProductsWithStockQuery,
  useLinkProductToInventoryMutation,
  useUnlinkProductFromInventoryMutation,
} = productsApi;